import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Marquee } from "@/components/site/Motion";
import {
  Compass, Mountain, Landmark, Palmtree, Tent, Users, ArrowRight, CalendarDays,
  Sparkles, HeartHandshake, ShieldCheck, Award, Route, Headset, ChevronRight, Search,
} from "lucide-react";
import { api, fileUrl, inr } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { TripCard } from "@/components/site/TripCard";
import { CreatorsFunky } from "@/components/site/CreatorsFunky";
import { FestiveBand } from "@/components/site/FestiveBand";
import { SpecialsFunky } from "@/components/site/SpecialsFunky";
import { orderedSections } from "@/lib/homeSections";
import { ReviewCard } from "@/components/site/Reviews";
import { StarRating } from "@/components/site/StarRating";
import { Seo } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1619796696652-a29a854f21a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const ICONS = { Compass, Mountain, Landmark, Palmtree, Tent, Users };

const WHY = [
  { icon: Sparkles, title: "Curated Experiences", desc: "Handpicked journeys designed by travellers, for travellers." },
  { icon: HeartHandshake, title: "Premium Hospitality", desc: "Comfortable stays, warm hosts and thoughtful details." },
  { icon: Route, title: "Hassle-Free Travel", desc: "Permits, transfers and logistics — all handled for you." },
  { icon: Award, title: "Experienced Team", desc: "Certified leaders with years on the trail." },
  { icon: Users, title: "Group & Private Trips", desc: "Join a group or design a private departure." },
  { icon: Headset, title: "Trusted Support", desc: "Real humans on WhatsApp before, during and after." },
];

export default function Home() {
  const navigate = useNavigate();
  const [heroQ, setHeroQ] = useState("");
  const { settings } = useSettings();
  const [trips, setTrips] = useState([]);
  const [cats, setCats] = useState([]);
  const [deps, setDeps] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [creators, setCreators] = useState([]);
  const [festive, setFestive] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 160]);
  const destinations = [...new Set(trips.map((t) => t.destination).filter(Boolean))];

  useEffect(() => {
    api.get("/trips").then(({ data }) => setTrips(data));
    api.get("/trips?special=true").then(({ data }) => setSpecials(data));
    api.get("/trips?creator=true").then(({ data }) => setCreators(data));
    api.get("/trips?festive=true").then(({ data }) => setFestive(data));
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/departures").then(({ data }) => setDeps(data.slice(0, 6)));
    api.get("/reviews?featured=true&limit=6").then(({ data }) => setReviews(data));
  }, []);

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : null;

  const SECTIONS = {
    marquee: (
      <>
        {/* DESTINATIONS MARQUEE */}
        {destinations.length > 2 && (
          <div className="border-y border-border bg-background py-3" data-testid="home-marquee">
            <Marquee items={destinations.map((d) => ({ label: d, to: `/trips?q=${encodeURIComponent(d)}` }))} />
          </div>
        )}
      </>
    ),
    festive: (
      <>
        {/* FESTIVE SPECIALS */}
        {festive.length > 0 && (
          <FestiveBand trips={festive} settings={settings} />
        )}
      </>
    ),
    creators: (
      <>
        {/* CREATOR TRIPS */}
        {creators.length > 0 && <CreatorsFunky trips={creators} settings={settings} />}
      </>
    ),
    upcoming: (
      <>
        {/* UPCOMING TRIPS */}
        <section id="upcoming" className="mx-auto max-w-7xl px-6 py-20 sm:py-28 scroll-mt-20">
          <Reveal className="flex flex-wrap items-end justify-between gap-3 mb-10">
            <div>
              <p className="text-primary font-medium tracking-wide uppercase text-sm">Upcoming Trips</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold mt-1">Departing soon</h2>
            </div>
            <Link to="/trips" className="hidden sm:block"><Button variant="ghost" className="rounded-full text-primary">All trips <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {trips.slice(0, 6).map((t, i) => <TripCard key={t.id} trip={t} index={i} />)}
          </div>
        </section>
      </>
    ),
    specials: (
      <>
        {/* SAFARSHALA SPECIALS */}
        {specials.length > 0 && <SpecialsFunky trips={specials} settings={settings} />}
      </>
    ),
    categories: (
      <>
        {/* CATEGORIES */}
        <section className="bg-muted/50 py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-primary font-medium tracking-wide uppercase text-sm">Ways to Wander</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold mt-1">Find your kind of journey</h2>
            </div>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {cats.map((c, i) => {
                const Icon = ICONS[c.icon] || Compass;
                return (
                  <motion.div key={c.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}>
                    <Link to={`/trips?category=${c.slug}`} data-testid={`category-${c.slug}`}
                      className="group flex items-center gap-4 rounded-2xl bg-card border border-border p-4 sm:p-6 hover:border-primary hover:-translate-y-1 transition-transform duration-300 min-w-0">
                      <div className="h-12 w-12 shrink-0 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg sm:text-xl leading-tight">{c.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 sm:line-clamp-1">{c.description}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </>
    ),
    departures: (
      <>
        {/* UPCOMING DEPARTURES */}
        <section className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-10">
            <div>
              <p className="text-primary font-medium tracking-wide uppercase text-sm">Mark your calendar</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold mt-1">Upcoming departures</h2>
            </div>
            <Link to="/calendar" className="hidden sm:block"><Button variant="ghost" className="rounded-full text-primary">Full calendar <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-w-0">
            {deps.map((d, i) => {
              const dt = new Date(d.date);
              const day = dt.getDate();
              const mon = dt.toLocaleString("en", { month: "short" }).toUpperCase();
              return (
                <motion.div key={d.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
                  className="flex items-center gap-4 rounded-2xl bg-card border border-border p-4 hover:shadow-lg transition-shadow min-w-0 overflow-hidden" data-testid="departure-item">
                  <div className="h-20 w-20 shrink-0 rounded-xl overflow-hidden relative">
                    <img src={fileUrl(d.cover_image)} alt={d.trip_name} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-primary font-mono text-sm"><CalendarDays className="h-4 w-4" /> {day} {mon}</div>
                    <h3 className="font-heading text-xl leading-tight truncate">{d.trip_name}</h3>
                    <p className="text-sm text-muted-foreground">{d.duration} · <span className="text-foreground font-medium">from {inr(d.from_price)}</span></p>
                  </div>
                  <Link to={`/trips/${d.trip_slug}`} className="shrink-0"><Button size="sm" variant="outline" className="rounded-full" data-testid="departure-view">View</Button></Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      </>
    ),
    why: (
      <>
        {/* WHY SAFARSHALA */}
        <section className="bg-secondary text-secondary-foreground py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal className="max-w-2xl mb-12">
              <p className="text-gold font-medium tracking-wide uppercase text-sm">Why {settings.brand_name || "Safarshala"}</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold mt-1">Travel that feels effortless</h2>
            </Reveal>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {WHY.map((w, i) => (
                <motion.div key={w.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                  className="border-l-2 border-gold/50 pl-5">
                  <w.icon className="h-8 w-8 text-gold mb-3" />
                  <h3 className="font-heading text-2xl">{w.title}</h3>
                  <p className="text-sm text-secondary-foreground/70 mt-1">{w.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </>
    ),
    reviews: (
      <>
        {/* TESTIMONIALS */}
        {reviews.length > 0 && (
          <section className="mx-auto max-w-7xl px-6 py-20 sm:py-28" data-testid="home-reviews">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-primary font-medium tracking-wide uppercase text-sm">What travellers say</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-semibold mt-1">Stories from the road</h2>
              </div>
              <div className="flex items-center gap-3 rounded-full border border-border px-4 py-2">
                <StarRating value={avg} />
                <span className="font-heading text-2xl">{avg}</span>
                <span className="text-sm text-muted-foreground">from happy travellers</span>
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {reviews.map((r, i) => <ReviewCard key={r.id} r={r} index={i} showTrip />)}
            </div>
          </section>
        )}
      </>
    ),
    cta: (
      <>
        {/* FINAL CTA */}
        <section className="relative py-24 overflow-hidden">
          <img src="https://images.unsplash.com/photo-1631420105765-caf5ccd069bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 mx-auto max-w-3xl px-6 text-center text-white">
            <h2 className="font-heading text-4xl sm:text-6xl font-semibold">Your next adventure is waiting.</h2>
            <p className="mt-4 text-white/85 text-lg">Explore upcoming trips and reserve your spot with just ₹{settings.advance_per_person || 1000} per person.</p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <Link to="/trips"><Button size="lg" className="rounded-full px-8 h-12" data-testid="cta-explore">Explore Trips</Button></Link>
              <Link to="/calendar"><Button size="lg" variant="outline" className="rounded-full px-8 h-12 bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white">See Departures</Button></Link>
            </div>
          </div>
        </section>
      </>
    ),
  };

  return (
    <div>
      <Seo />
      {/* HERO */}
      <section className="relative min-h-[92svh] w-full overflow-hidden flex" data-testid="home-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-[-10%_0]">
          <motion.img
            src={HERO_IMG} alt="Himalayan sunrise" initial={{ scale: 1.15 }} animate={{ scale: 1 }}
            transition={{ duration: 8, ease: "easeOut" }} className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 flex flex-col justify-end pt-32 sm:pt-36 pb-28 sm:pb-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="uppercase tracking-[0.3em] text-white/80 text-xs sm:text-sm mb-4">
            {settings.brand_name || "Safarshala"} · Curated Indian Journeys
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7 }}
            className="font-heading font-semibold text-white text-4xl sm:text-6xl lg:text-[4.5rem] max-w-3xl leading-[1.05] text-balance">
            Journeys to the wild, the sacred & the unforgettable.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="mt-5 text-white/85 text-base sm:text-lg max-w-xl">
            Treks, trips and dham yatras across India — thoughtfully crafted, premium in feel, effortless to book.
          </motion.p>
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
            onSubmit={(e) => { e.preventDefault(); navigate(`/trips${heroQ.trim() ? `?q=${encodeURIComponent(heroQ.trim())}` : ""}`); }}
            className="mt-8 flex items-center gap-2 rounded-full bg-white/95 dark:bg-card p-1.5 pl-4 shadow-2xl shadow-black/30 max-w-xl" data-testid="hero-search-form">
            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
            <input value={heroQ} onChange={(e) => setHeroQ(e.target.value)} placeholder="Search trips, destinations or experiences…" aria-label="Search trips"
              className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground text-base outline-none min-w-0 h-11" data-testid="hero-search-input" />
            <Button type="submit" size="lg" className="rounded-full h-11 px-5 sm:px-7" data-testid="hero-search-btn">Search</Button>
          </motion.form>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
            className="mt-4 flex flex-wrap gap-3">
            <Link to="/trips"><Button size="lg" className="rounded-full text-base px-8 h-12 btn-shine" data-testid="hero-explore">Explore Trips <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/calendar"><Button size="lg" variant="outline" className="rounded-full text-base px-8 h-12 bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white" data-testid="hero-departures">View Upcoming Departures</Button></Link>
          </motion.div>
        </div>
        <motion.a href="#upcoming" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-white/70 text-[10px] uppercase tracking-[0.3em]" data-testid="hero-scroll-cue" aria-label="Scroll down">
          <span className="h-9 w-5 rounded-full border border-white/50 flex items-start justify-center p-1"><span className="h-1.5 w-1.5 rounded-full bg-white scroll-dot" /></span>
        </motion.a>
      </section>

      {orderedSections(settings.home_sections).map((sec) => <Fragment key={sec.key}>{SECTIONS[sec.key]}</Fragment>)}
    </div>
  );
}
